import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAssignedClass } from "../hooks/use-assigned-class.ts";
import { openClassPicker } from "../hooks/use-class-picker.ts";
import { useClasses } from "../hooks/use-classes.ts";
import { ClassSearch } from "./class-search.tsx";
import { MobileMenu } from "./mobile-menu.tsx";
import { ClassHeadshot } from "./overview/class-headshot.tsx";

const ROLE_LINKS = [
	{ to: "/", label: "OVERVIEW" },
	{ to: "/scout", label: "SCOUT" },
	{ to: "/support", label: "SUPPORT" },
	{ to: "/offense", label: "OFFENSE" },
	{ to: "/wildcard", label: "WILDCARD" },
] as const;

const linkBase =
	"font-headline text-sm uppercase tracking-tighter text-outline hover:text-on-surface transition-colors";
const linkActive =
	"font-headline text-sm uppercase tracking-tighter text-primary-container border-b-2 border-primary-container pb-1";

const iconButtonBase =
	"inline-grid h-8 w-8 shrink-0 place-items-center leading-none text-outline transition-all hover:bg-surface-container-highest hover:text-on-surface active:translate-x-0.5 active:translate-y-0.5";
const iconButtonActive =
	"inline-grid h-8 w-8 shrink-0 place-items-center leading-none text-primary-container";

const mobileIconButton =
	"inline-grid h-11 w-11 shrink-0 place-items-center leading-none text-outline transition-all hover:bg-surface-container-highest hover:text-on-surface active:translate-x-0.5 active:translate-y-0.5 md:hidden";

export function SiteHeader() {
	const [menuOpen, setMenuOpen] = useState(false);

	useEffect(() => {
		const mql = window.matchMedia("(min-width: 768px)");
		const handler = (e: MediaQueryListEvent) => {
			if (e.matches) {
				setMenuOpen(false);
			}
		};
		mql.addEventListener("change", handler);
		return () => {
			mql.removeEventListener("change", handler);
		};
	}, []);

	return (
		<>
			<header className="flex w-full items-center justify-between border-b-4 border-primary-container bg-surface px-4 py-3 shadow-[4px_4px_0_0_rgba(0,0,0,1)] md:px-6">
				<div className="flex min-w-0 items-center gap-8">
					<Link
						to="/"
						className="truncate font-headline text-base font-black tracking-widest text-primary-container uppercase hover:text-primary md:text-xl"
					>
						<span className="md:hidden">{"CLASS WARFARE"}</span>
						<span className="hidden md:inline">
							{"HELLDIVERS 2 // CLASS WARFARE"}
						</span>
					</Link>
					<nav className="hidden gap-6 md:flex">
						{ROLE_LINKS.map((link) => (
							<Link
								key={link.to}
								to={link.to}
								className={linkBase}
								activeProps={{ className: linkActive }}
								activeOptions={{ exact: link.to === "/" }}
							>
								{link.label}
							</Link>
						))}
					</nav>
				</div>
				<div className="flex items-center gap-4">
					<div className="hidden md:block">
						<ClassSearch />
					</div>
					<Link
						to="/owned-items"
						aria-label="Owned items"
						className={`${iconButtonBase} hidden md:inline-grid`}
						activeProps={{
							className: `${iconButtonActive} hidden md:inline-grid`,
						}}
					>
						<span className="material-symbols-outlined block text-[20px] leading-none">
							military_tech
						</span>
					</Link>
					<Link
						to="/settings"
						aria-label="Settings"
						className={`${iconButtonBase} hidden md:inline-grid`}
						activeProps={{
							className: `${iconButtonActive} hidden md:inline-grid`,
						}}
					>
						<span className="material-symbols-outlined block text-[20px] leading-none">
							settings
						</span>
					</Link>
					<AssignedClassAvatar />
					<button
						type="button"
						aria-label="Open menu"
						aria-expanded={menuOpen}
						onClick={() => {
							setMenuOpen(true);
						}}
						className={mobileIconButton}
					>
						<span className="material-symbols-outlined block text-[24px] leading-none">
							menu
						</span>
					</button>
				</div>
			</header>
			<MobileMenu
				open={menuOpen}
				onClose={() => {
					setMenuOpen(false);
				}}
			/>
		</>
	);
}

function AssignedClassAvatar() {
	const [assigned] = useAssignedClass();
	const { classes } = useClasses();

	if (!assigned) {
		return (
			<button
				type="button"
				onClick={openClassPicker}
				aria-label="No class assigned — choose one"
				title="Choose a class"
				className="flex h-11 w-11 cursor-pointer items-center justify-center border border-outline-variant bg-surface-container-highest text-outline transition-all hover:border-outline hover:text-primary active:translate-x-0.5 active:translate-y-0.5 md:h-8 md:w-8"
			>
				<span className="material-symbols-outlined text-lg">person</span>
			</button>
		);
	}

	const cls = classes.find((c) => c.slug === assigned.slug);
	const themeColorHex = cls?.themeColorHex ?? "#ffd165";

	return (
		<Link
			to="/classes/$slug"
			params={{ slug: assigned.slug }}
			aria-label={`Assigned class: ${assigned.name}`}
			title={`${assigned.name} // ${assigned.role}`}
			style={{ borderColor: themeColorHex }}
			className="block h-11 w-11 overflow-hidden border-2 bg-surface-container-highest transition-all hover:brightness-125 active:translate-x-0.5 active:translate-y-0.5 md:h-8 md:w-8"
		>
			<ClassHeadshot
				slug={assigned.slug}
				name={assigned.name}
				themeColorHex={themeColorHex}
				size="sm"
				className="h-full w-full"
				showBorder={false}
			/>
		</Link>
	);
}
